import type { z } from 'zod';
import { apiErrorSchema, CSRF_HEADER, CSRF_HEADER_VALUE, type ErrorCode } from '@indusketch/shared';
import { currentI18n } from '../../i18n/i18n';

export type ClientErrorCode = ErrorCode | 'NETWORK' | 'INVALID_RESPONSE' | 'VIEW_ONLY';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ClientErrorCode,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** The server answers errors in the language chosen in the app, not the browser's. */
function baseHeaders(): Record<string, string> {
  return { [CSRF_HEADER]: CSRF_HEADER_VALUE, 'accept-language': currentI18n().locale };
}

const networkError = () => new ApiError(0, 'NETWORK', currentI18n().m.network.offline);

/**
 * "View only" (administrators looking into projects): no request may change projects, assets
 * or activity on the server. Sign-in, own account settings and user administration stay
 * possible; reading is never blocked.
 */
let viewOnly: () => boolean = () => false;
export const setViewOnlyCheck = (check: () => boolean): void => {
  viewOnly = check;
};

function viewOnlyRefusal(method: Method, path: string): ApiError | null {
  if (method === 'GET' || /^\/api\/(auth|admin)\//.test(path) || !viewOnly()) return null;
  return new ApiError(0, 'VIEW_ONLY', currentI18n().m.inspect.blocked);
}

function serverError(status: number, json: unknown): ApiError {
  const parsed = apiErrorSchema.safeParse(json);
  if (parsed.success) {
    const { code, message, details } = parsed.data.error;
    return new ApiError(status, code, message, details);
  }
  const { m, fmt } = currentI18n();
  return new ApiError(status, 'INTERNAL', fmt(m.network.serverError, { status }));
}

/**
 * Every response is validated against the shared contract, so a server/client version
 * mismatch surfaces as a clear error instead of undefined behaviour deep in the UI.
 */
export async function apiRequest<T>(
  method: Method,
  path: string,
  schema: z.ZodType<T> | null,
  body?: unknown,
): Promise<T> {
  const refused = viewOnlyRefusal(method, path);
  if (refused) throw refused;
  const headers = baseHeaders();
  // FormData sets its own multipart boundary header.
  const isForm = body instanceof FormData;
  if (body !== undefined && !isForm) headers['content-type'] = 'application/json';

  let res: Response;
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? null : isForm ? body : JSON.stringify(body),
    });
  } catch {
    throw networkError();
  }

  const text = await res.text();
  const json: unknown = text ? safeJson(text) : undefined;

  if (!res.ok) throw serverError(res.status, json);

  if (schema === null) return undefined as T;
  const parsed = schema.safeParse(json);
  if (!parsed.success)
    throw new ApiError(res.status, 'INVALID_RESPONSE', currentI18n().m.network.invalidResponse);
  return parsed.data;
}

/** POST with JSON body and a binary response (e.g. the PDF report). Errors are JSON as usual. */
export async function apiRequestBlob(path: string, body: unknown): Promise<Blob> {
  let res: Response;
  try {
    res = await fetch(path, {
      method: 'POST',
      headers: { ...baseHeaders(), 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
  } catch {
    throw networkError();
  }
  if (!res.ok) throw serverError(res.status, safeJson(await res.text()));
  return res.blob();
}

/**
 * POST of a large form (e.g. a video) with upload progress (0 … 1); `fetch` cannot report
 * that. Response and errors are handled like `apiRequest`.
 */
export function apiUpload<T>(
  path: string,
  form: FormData,
  schema: z.ZodType<T>,
  onProgress: (fraction: number) => void,
): Promise<T> {
  const refused = viewOnlyRefusal('POST', path);
  if (refused) return Promise.reject(refused);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', path);
    for (const [k, v] of Object.entries(baseHeaders())) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
    };
    xhr.onerror = () => reject(networkError());
    xhr.onload = () => {
      const json = xhr.responseText ? safeJson(xhr.responseText) : undefined;
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(serverError(xhr.status, json));
        return;
      }
      const parsed = schema.safeParse(json);
      if (parsed.success) resolve(parsed.data);
      else
        reject(
          new ApiError(xhr.status, 'INVALID_RESPONSE', currentI18n().m.network.invalidResponse),
        );
    };
    xhr.send(form);
  });
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}
